import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/** sessionStorage key holding the last automatic chunk-recovery reload time. */
export const CHUNK_RELOAD_KEY = 'suturia:chunk-reload';

/** Ignore a second auto-reload inside this window (avoids infinite loops). */
export const CHUNK_RELOAD_COOLDOWN_MS = 15_000;

/** In-memory fallback when sessionStorage is blocked (private mode, etc.). */
let memoryReloadAt = 0;

/** True for browser "stale hashed chunk" / failed dynamic import errors. */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return (
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    (err instanceof TypeError && /dynamically imported module|module script/i.test(msg))
  );
}

function readReloadAt(storage: Pick<Storage, 'getItem'> | null): number {
  if (!storage) return memoryReloadAt;
  try {
    const raw = storage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return memoryReloadAt;
    const n = Number(raw);
    return Number.isFinite(n) ? n : memoryReloadAt;
  } catch {
    return memoryReloadAt;
  }
}

/** True when a recovery reload was triggered recently (Sentry beforeSend). */
export function wasChunkReloadRecent(
  storage: Pick<Storage, 'getItem'> | null = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : null,
  now = Date.now(),
  cooldownMs = CHUNK_RELOAD_COOLDOWN_MS,
): boolean {
  const last = readReloadAt(storage);
  return last > 0 && now - last < cooldownMs;
}

/**
 * One-shot hard reload after a stale-chunk failure. Returns true when a reload
 * was scheduled (caller should not surface the error). Returns false if we
 * already reloaded recently — then CrashFallback can show.
 */
export function tryRecoverFromStaleChunk(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : null,
  reload: () => void = () => {
    window.location.reload();
  },
  now = Date.now(),
  cooldownMs = CHUNK_RELOAD_COOLDOWN_MS,
): boolean {
  if (wasChunkReloadRecent(storage, now, cooldownMs)) return false;
  memoryReloadAt = now;
  if (storage) {
    try {
      storage.setItem(CHUNK_RELOAD_KEY, String(now));
    } catch {
      /* private mode / quota — memory marker still blocks loops */
    }
  }
  reload();
  return true;
}

/** Drop expired recovery markers so a later tab session can recover again. */
export function clearChunkReloadFlag(
  storage: Pick<Storage, 'getItem' | 'removeItem'> | null = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : null,
  now = Date.now(),
  cooldownMs = CHUNK_RELOAD_COOLDOWN_MS,
): void {
  const last = readReloadAt(storage);
  if (last > 0 && now - last < cooldownMs) return;
  memoryReloadAt = 0;
  if (!storage) return;
  try {
    storage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    /* private mode / quota */
  }
}

/** Retry a dynamic import a few times before surfacing the failure. */
export async function importWithRetry<T>(
  factory: () => Promise<T>,
  retries = 2,
): Promise<T> {
  let last: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await factory();
    } catch (err) {
      last = err;
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
      }
    }
  }
  // Same hashed URL will keep 404-ing after a deploy. Reload once so the
  // browser picks up a fresh index.html (and updated service worker).
  if (isChunkLoadError(last) && tryRecoverFromStaleChunk()) {
    // Hang briefly so Suspense keeps the splash; if reload never fires
    // (blocked navigation, flaky WebView), reject so CrashFallback shows
    // instead of an infinite blank screen.
    return new Promise<T>((_, reject) => {
      setTimeout(() => reject(last), 2_500);
    });
  }
  throw last;
}

/**
 * Like React.lazy, but retries transient dynamic-import failures (stale chunk
 * hashes after deploy, flaky mobile networks). Without this, a single failed
 * tab switch hits the global CrashFallback ("Algo ha ido mal").
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's ComponentType<any>
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2,
): LazyExoticComponent<T> {
  return lazy(() => importWithRetry(factory, retries));
}

type ChunkRecoveryDeps = {
  window?: Window & typeof globalThis;
  recover?: () => boolean;
};

/**
 * Safety net for Mobile Safari (SUTURIA-WEB-8): module-script failures that
 * bypass React.lazy / ErrorBoundary — boot-time locale imports, modulepreload,
 * and other fire-and-forget dynamic imports — surface as unhandledrejection
 * with no stack. Catch them early and hard-reload once onto a fresh shell.
 *
 * Install before Sentry.init so the recovery marker is set before beforeSend.
 */
export function installChunkLoadRecovery(deps: ChunkRecoveryDeps = {}): () => void {
  const win = deps.window ?? (typeof window !== 'undefined' ? window : undefined);
  if (!win) return () => {};
  const recover = deps.recover ?? (() => tryRecoverFromStaleChunk());

  const onRejection = (event: PromiseRejectionEvent) => {
    if (!isChunkLoadError(event.reason)) return;
    if (recover()) {
      event.preventDefault();
    }
  };

  const onError = (event: ErrorEvent) => {
    // Safari sometimes reports the same failure as a script error (message
    // only — no Error instance on event.error).
    const err = event.error ?? event.message;
    if (!isChunkLoadError(err)) return;
    if (recover()) {
      event.preventDefault();
    }
  };

  win.addEventListener('unhandledrejection', onRejection);
  win.addEventListener('error', onError);
  return () => {
    win.removeEventListener('unhandledrejection', onRejection);
    win.removeEventListener('error', onError);
  };
}
